import React, { useCallback, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { supabase } from '../supabase';
import {
  clearSupabaseFetchDebugEvents,
  getSupabaseFetchDebugEvents,
  filterProfilesRestEvents,
  setActiveFetchCorrelation,
  clearActiveFetchCorrelation,
} from '../supabaseFetchDebug';
import { readSupabaseAuthAccessTokenFromLocalStorage } from '../supabaseProfilesDiag';
import { devLog } from '../utils/logger';

const PREFIX = '[SUPABASE_PIPELINE_DIAG]';
const TEST_TIMEOUT_MS = 15000;

function decodeJwtSub(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') return null;
  try {
    const part = accessToken.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(pad);
    const json = atob(padded);
    const payload = JSON.parse(json);
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

function withTimeout(promise, ms, label) {
  let t;
  const timeoutP = new Promise((_, reject) => {
    t = setTimeout(() => reject(new Error(`${label}: timeout after ${ms}ms`)), ms);
  });
  return Promise.race([promise, timeoutP]).finally(() => clearTimeout(t));
}

/** Payload stats for comparing with Dashboard saveClient (JSON-serializable rows only). */
function summarizeClientsPayloadForLog(clients) {
  const list = Array.isArray(clients) ? clients : [];
  let jsonLen = -1;
  let stringifyError = null;
  try {
    jsonLen = JSON.stringify({ clients: list }).length;
  } catch (e) {
    stringifyError = String(e);
  }
  const keysFirst5 = list.slice(0, 5).map((c, idx) => ({
    idx,
    id: c && typeof c === 'object' ? c.id : null,
    keys: c && typeof c === 'object' && !Array.isArray(c) ? Object.keys(c) : [],
  }));
  return { clientCount: list.length, jsonLen, stringifyError, keysFirst5 };
}

export default function DevSupabasePipelineDiag() {
  const [logLines, setLogLines] = useState(() => [
    `${PREFIX} Open Chrome DevTools → Network; filter "profiles" or "rest/v1".`,
    `${PREFIX} Compare console [SUPABASE_GLOBAL_FETCH] + this log with Network rows (pending/cancelled/status).`,
  ]);
  const [running, setRunning] = useState(false);

  const add = useCallback((msg) => {
    const line = `${new Date().toISOString()} ${msg}`;
    setLogLines((prev) => [...prev, line]);
    devLog(PREFIX, msg);
  }, []);

  const runSuite = useCallback(async () => {
    setRunning(true);
    try {
      const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').trim();
      const anonKey = (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
      if (!supabase || !supabaseUrl || !anonKey) {
        add('Aborted: missing supabase client or env URL/key.');
        return;
      }

      const token = readSupabaseAuthAccessTokenFromLocalStorage(supabaseUrl);
      const userId = token ? decodeJwtSub(token) : null;
      if (!token || !userId) {
        add('Aborted: no access_token in localStorage or JWT sub missing (sign in first).');
        return;
      }

      const base = supabaseUrl.replace(/\/$/, '');
      const rawFetch = typeof fetch !== 'undefined' && typeof window !== 'undefined' ? fetch.bind(window) : fetch;

      const summarizeWrapper = (label) => {
        const all = getSupabaseFetchDebugEvents();
        const prof = filterProfilesRestEvents(all);
        const starts = all.filter((e) => e.phase === 'start');
        const profStarts = prof.filter((e) => e.phase === 'start');
        add(
          `${label}: [SUPABASE_GLOBAL_FETCH] wrapper saw fetch starts total=${starts.length} profiles-rest=${profStarts.length} (cross-check DevTools Network)`
        );
      };

      add('--- Setup: direct GET clients (raw fetch, not supabase-js) ---');
      let existingClients = [];
      try {
        const setupUrl = `${base}/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=clients`;
        const setupRes = await withTimeout(
          rawFetch(setupUrl, {
            method: 'GET',
            headers: {
              apikey: anonKey,
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
          }),
          TEST_TIMEOUT_MS,
          'setup GET clients'
        );
        const setupText = await setupRes.text();
        add(`Setup: finished status=${setupRes.status} bodyLen=${setupText.length}`);
        if (setupRes.ok) {
          try {
            const rows = JSON.parse(setupText);
            existingClients = Array.isArray(rows?.[0]?.clients) ? rows[0].clients : [];
          } catch {
            existingClients = [];
          }
        }
        add(`Setup: clients array length=${existingClients.length} (no schema change; PATCH uses same payload)`);
      } catch (e) {
        add(`Setup: threw ${/** @type {{ name?: string }} */ (e).name}: ${String(/** @type {{ message?: string }} */ (e).message || e)}`);
        return;
      }

      await new Promise((r) => setTimeout(r, 300));

      clearSupabaseFetchDebugEvents();
      add('--- A: supabase-js .from(profiles).select(id).eq().maybeSingle() ---');
      try {
        const result = await withTimeout(
          supabase.from('profiles').select('id').eq('id', userId).maybeSingle(),
          TEST_TIMEOUT_MS,
          'A select'
        );
        summarizeWrapper('A');
        add(`A: finished error=${result.error?.message || 'none'} hasData=${!!result.data}`);
      } catch (e) {
        summarizeWrapper('A');
        add(`A: threw ${/** @type {{ name?: string }} */ (e).name}: ${String(/** @type {{ message?: string }} */ (e).message || e)}`);
      }

      await new Promise((r) => setTimeout(r, 400));

      clearSupabaseFetchDebugEvents();
      add('--- B: supabase-js .from(profiles).update({ clients }).eq() ---');
      const bPayload = summarizeClientsPayloadForLog(existingClients);
      add(
        `B: payload JSON.stringify({clients}).length=${bPayload.jsonLen} clientCount=${bPayload.clientCount} stringifyError=${bPayload.stringifyError || 'none'}`
      );
      add(`B: Object.keys first 5 clients: ${JSON.stringify(bPayload.keysFirst5)}`);
      const corrB = `pipeline_B_${Date.now().toString(36)}`;
      setActiveFetchCorrelation(corrB);
      add(`B: correlation id for [SUPABASE_GLOBAL_FETCH]: ${corrB}`);
      try {
        const result = await withTimeout(
          supabase.from('profiles').update({ clients: existingClients }).eq('id', userId),
          TEST_TIMEOUT_MS,
          'B update'
        );
        summarizeWrapper('B');
        add(`B: finished error=${result.error?.message || 'none'}`);
      } catch (e) {
        summarizeWrapper('B');
        add(`B: threw ${/** @type {{ name?: string }} */ (e).name}: ${String(/** @type {{ message?: string }} */ (e).message || e)}`);
      } finally {
        clearActiveFetchCorrelation();
      }

      await new Promise((r) => setTimeout(r, 400));

      clearSupabaseFetchDebugEvents();
      add('--- C: raw fetch PATCH /rest/v1/profiles (bypasses supabase-js) ---');
      try {
        const patchUrl = `${base}/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}`;
        const body = JSON.stringify({ clients: existingClients });
        const patchRes = await withTimeout(
          rawFetch(patchUrl, {
            method: 'PATCH',
            headers: {
              apikey: anonKey,
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Prefer: 'return=minimal',
            },
            body,
          }),
          TEST_TIMEOUT_MS,
          'C PATCH'
        );
        const patchText = await patchRes.text();
        const wrapAfter = getSupabaseFetchDebugEvents();
        add(
          `C: finished status=${patchRes.status} bodyLen=${patchText.length} wrapperStartsDuringC=${wrapAfter.filter((e) => e.phase === 'start').length} (expect 0)`
        );
      } catch (e) {
        const wrapAfter = getSupabaseFetchDebugEvents();
        add(
          `C: threw ${/** @type {{ name?: string }} */ (e).name}: ${String(/** @type {{ message?: string }} */ (e).message || e)} wrapperStarts=${wrapAfter.filter((x) => x.phase === 'start').length}`
        );
      }

      add('--- Done. If A/B show no [SUPABASE_GLOBAL_FETCH] start for PATCH but C works → hang before fetch. If starts but no response → pending fetch. ---');
    } finally {
      setRunning(false);
    }
  }, [add]);

  if (process.env.NODE_ENV !== 'development') {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ fontSize: '1.25rem' }}>Supabase pipeline diagnostic (dev only)</h1>
      <p style={{ fontSize: 14, color: '#444' }}>
        Route: <code>/dev/supabase-pipeline</code>. Sign in first. Uses the same browser Supabase singleton; dev builds wrap{' '}
        <code>global.fetch</code> for that client (see <code>src/supabase.js</code>).
      </p>
      <p style={{ fontSize: 14 }}>
        <Link to="/dashboard/overview">← Back to dashboard</Link>
      </p>
      <button type="button" disabled={running} onClick={() => void runSuite()} style={{ padding: '8px 16px', marginBottom: 16 }}>
        {running ? 'Running…' : 'Run A → B → C (sequential)'}
      </button>
      <pre
        style={{
          background: '#111',
          color: '#e6e6e6',
          padding: 16,
          fontSize: 12,
          overflow: 'auto',
          maxHeight: '70vh',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {logLines.join('\n')}
      </pre>
    </div>
  );
}
